import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Carrega dado assíncrono com proteção contra resposta fora de ordem: cada
 * execução recebe um número de sequência e só a mais recente pode escrever no
 * state. Sem isso, trocar de filtro rápido faz a resposta lenta da requisição
 * antiga sobrescrever a nova (PRD §9-B pede estado de carregamento correto).
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const seq = useRef(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    const mine = ++seq.current;
    let alive = true;
    setLoading(true);
    setError(null);

    fnRef.current()
      .then((result) => {
        if (!alive || mine !== seq.current) return;
        setData(result);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!alive || mine !== seq.current) return;
        setError(err instanceof ApiError ? err.message : 'Falha inesperada ao carregar.');
        setLoading(false);
      });

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, loading, error, reload };
}
